import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { listMyProgress, listMyBookmarks, getMyStreak } from "@/lib/academy.functions";
import { Flame, Bookmark, TrendingUp } from "lucide-react";

export const Route = createFileRoute("/_authenticated/academy/dashboard")({
  head: () => ({ meta: [{ title: "Academy Dashboard · KAfrequency" }, { name: "robots", content: "noindex" }] }),
  component: Dashboard,
});

function Dashboard() {
  const progress = useQuery({ queryKey: ["academy", "progress"], queryFn: () => listMyProgress() });
  const bookmarks = useQuery({ queryKey: ["academy", "bookmarks"], queryFn: () => listMyBookmarks() });
  const streak = useQuery({ queryKey: ["academy", "streak"], queryFn: () => getMyStreak() });

  const completedCount = (progress.data ?? []).filter((p) => p.status === "completed").length;
  const inProgressCount = (progress.data ?? []).filter((p) => p.status === "in_progress").length;

  return (
    <div className="min-h-dvh bg-obsidian text-foreground">
      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold">Academy</p>
            <h1 className="mt-2 font-serif text-4xl text-gold-luminous">Your practice, today.</h1>
          </div>
          <Link to="/academy" className="text-sm text-gold">All domains →</Link>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border hairline p-5">
            <div className="flex items-center gap-2 text-gold"><Flame size={16} /> <span className="text-xs uppercase tracking-widest">Streak</span></div>
            <p className="mt-2 font-serif text-4xl">{streak.data?.current_streak ?? 0}</p>
            <p className="text-xs text-foreground/60">Longest {streak.data?.longest_streak ?? 0}</p>
          </div>
          <div className="rounded-2xl border hairline p-5">
            <div className="flex items-center gap-2 text-gold"><TrendingUp size={16} /> <span className="text-xs uppercase tracking-widest">XP</span></div>
            <p className="mt-2 font-serif text-4xl">{streak.data?.xp ?? 0}</p>
            <p className="text-xs text-foreground/60">{completedCount} lessons complete</p>
          </div>
          <div className="rounded-2xl border hairline p-5">
            <div className="flex items-center gap-2 text-gold"><Bookmark size={16} /> <span className="text-xs uppercase tracking-widest">Saved</span></div>
            <p className="mt-2 font-serif text-4xl">{bookmarks.data?.length ?? 0}</p>
            <p className="text-xs text-foreground/60">{inProgressCount} in progress</p>
          </div>
        </div>

        <section className="mt-10">
          <h2 className="font-serif text-2xl text-gold-luminous">In progress</h2>
          <ul className="mt-4 space-y-2">
            {(progress.data ?? []).filter((p) => p.status === "in_progress").slice(0, 8).map((p) => (
              <li key={p.lesson_id} className="rounded-lg border hairline p-3 text-sm">
                <span className="text-foreground/70">Lesson</span> · <span>{p.progress_pct}%</span>
              </li>
            ))}
            {(progress.data ?? []).filter((p) => p.status === "in_progress").length === 0 && (
              <li className="text-sm text-foreground/60">Nothing in progress. <Link to="/academy" className="text-gold">Start a path →</Link></li>
            )}
          </ul>
        </section>

        <section className="mt-10">
          <h2 className="font-serif text-2xl text-gold-luminous">Bookmarked lessons</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {(bookmarks.data ?? []).map((b: any) => (
              <li key={b.lesson_id} className="rounded-lg border hairline p-3">
                <p className="font-serif text-base">{b.academy_lessons?.title ?? "Lesson"}</p>
                {b.academy_lessons?.summary && <p className="mt-1 line-clamp-2 text-xs text-foreground/60">{b.academy_lessons.summary}</p>}
              </li>
            ))}
            {(bookmarks.data ?? []).length === 0 && (
              <li className="text-sm text-foreground/60">No bookmarks yet.</li>
            )}
          </ul>
        </section>
      </section>
    </div>
  );
}
